import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { QuantityStepper } from "./QuantityStepper";
import { removeLine, updateQuantity, type CartLine } from "@/lib/cart/store";
import { formatPrice } from "@/lib/cart/pricing";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackProductImage } from "@/lib/catalog/fallback-images";

export function CartLineRow({
  line,
  variant = "page",
  onNavigate,
}: {
  line: CartLine;
  variant?: "page" | "mini";
  onNavigate?: () => void;
}) {
  const compact = variant === "mini";
  const lineTotal = line.unitPrice * line.quantity;
  const pack = line.pack;

  return (
    <div className={"flex gap-4 " + (compact ? "py-4" : "py-6")}>
      {pack ? (
        <Link to="/packs/$slug" params={{ slug: pack.packSlug }} onClick={onNavigate} className="shrink-0">
          <SafeImage
            src={line.img}
            fallback={fallbackProductImage(line.slug)}
            alt={line.name}
            className={"object-cover " + (compact ? "h-20 w-20" : "h-28 w-28 sm:h-32 sm:w-32")}
            loading="lazy"
          />
        </Link>
      ) : (
        <Link
          to="/fragrances/$slug"
          params={{ slug: line.slug }}
          onClick={onNavigate}
          className="shrink-0"
        >
          <SafeImage
            src={line.img}
            fallback={fallbackProductImage(line.slug)}
            alt={line.name}
            className={"object-cover " + (compact ? "h-20 w-20" : "h-28 w-28 sm:h-32 sm:w-32")}
            loading="lazy"
          />
        </Link>
      )}

      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={pack ? "/packs/$slug" : "/fragrances/$slug"}
              params={{ slug: pack ? pack.packSlug : line.slug }}
              onClick={onNavigate}
              className="block truncate text-sm font-medium text-black transition hover:text-[#C6A15B] sm:text-base"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {line.name}
            </Link>
            <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-black/50">
              {pack ? `${pack.variantLabel} · ${pack.packName}` : line.variantLabel}
            </p>
            {pack && (
              <div className="mt-2">
                <p className="text-[11px] uppercase tracking-[0.18em] text-black/45">
                  {pack.bottleCount} bottles · {pack.bottleSizeMl} ML
                  {pack.bottlesPerBox > 0
                    ? ` · ${Math.ceil(pack.bottleCount / pack.bottlesPerBox)} box${
                        Math.ceil(pack.bottleCount / pack.bottlesPerBox) === 1 ? "" : "es"
                      }`
                    : ""}
                </p>
                <ul className="mt-1 space-y-0.5 text-[11px] text-black/55">
                  {(pack.fixedItems ?? []).map((item, i) => (
                    <li key={`fx-${i}`} className="truncate">
                      {item.qty} × {item.label}
                    </li>
                  ))}
                  {pack.bottles.slice(0, compact ? 3 : pack.bottles.length).map((b) => (
                    <li key={b.productId} className="truncate">
                      {b.qty} × {b.name}
                    </li>
                  ))}
                  {compact && pack.bottles.length > 3 && (
                    <li className="text-black/40">+ {pack.bottles.length - 3} more fragrances</li>
                  )}
                </ul>
                {(pack.addons ?? []).length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-[11px] text-black/45">
                    {pack.addons!.map((a) => (
                      <li key={a.addonId} className="truncate">
                        + {a.label}
                        {a.qty > 1 ? ` × ${a.qty}` : ""}
                        {a.lineTotal > 0 ? ` (${formatPrice(a.lineTotal)})` : ""}
                      </li>
                    ))}
                  </ul>
                )}
                {pack.gift && (pack.gift.recipientName || pack.gift.message) && (
                  <p className="mt-1 text-[11px] italic text-black/50">
                    {pack.gift.recipientName ? `Gift for ${pack.gift.recipientName}. ` : ""}
                    {pack.gift.message ? `“${pack.gift.message}”` : ""}
                  </p>
                )}
                {pack.discount > 0 && (
                  <p className="mt-1 text-[11px] font-medium text-[#C6A15B]">
                    You save {formatPrice(pack.discount)} on this pack
                  </p>
                )}
                <Link
                  to="/packs/$slug"
                  params={{ slug: pack.packSlug }}
                  search={{ edit: line.variantId }}
                  onClick={onNavigate}
                  className="mt-2 inline-block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C6A15B] underline-offset-4 hover:underline"
                >
                  Edit pack
                </Link>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => removeLine(line.productId, line.variantId)}
            aria-label={`Remove ${line.name}`}
            className="inline-flex h-8 w-8 items-center justify-center text-black/50 transition hover:text-black"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 flex items-end justify-between gap-3">
          <QuantityStepper
            value={line.quantity}
            onChange={(n) => updateQuantity(line.productId, line.variantId, n)}
            label={`Quantity for ${line.name}`}
          />
          <div className="text-right">
            <p className="text-sm font-medium tabular-nums text-black">{formatPrice(lineTotal)}</p>
            {line.quantity > 1 && (
              <p className="text-[11px] tabular-nums text-black/50">
                {formatPrice(line.unitPrice)} each
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
